import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import AppLayout from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Check, Clock, Plus, Snowflake, ListChecks, Sparkles, Leaf } from "lucide-react";
import { toast } from "sonner";

const PRIORITY_TONE = {
  high: "bg-amber-50 text-amber-700 border-amber-200",
  normal: "bg-slate-100 text-slate-700 border-slate-200",
  low: "bg-slate-50 text-slate-500 border-slate-200",
};

export default function TasksPage() {
  const { t } = useI18n();
  const [tasks, setTasks] = useState([]);
  const [tab, setTab] = useState("open");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", category: "Allgemein", priority: "normal", estimated_minutes: 5 });

  const load = async () => {
    try {
      const { data } = await api.get("/tasks");
      setTasks(data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    load();
    const onChange = () => load();
    window.addEventListener("ag:tasks-changed", onChange);
    return () => window.removeEventListener("ag:tasks-changed", onChange);
  }, []);

  const updateStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/tasks/${id}`, { status });
      setTasks((arr) => arr.map((t) => (t.id === id ? data : t)));
      toast.success(status === "done" ? "Erledigt." : status === "later" ? "Auf später verschoben." : "Aktualisiert.");
    } catch {
      toast.error("Konnte Aufgabe nicht aktualisieren");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/tasks", { ...form, estimated_minutes: Number(form.estimated_minutes) || 5 });
      setTasks((arr) => [data, ...arr]);
      setOpen(false);
      setForm({ title: "", description: "", category: "Allgemein", priority: "normal", estimated_minutes: 5 });
      toast.success("Aufgabe hinzugefügt.");
    } catch {
      toast.error("Konnte Aufgabe nicht erstellen");
    }
  };

  const filtered = tasks.filter((task) => (tab === "all" ? true : task.status === tab));

  return (
    <AppLayout>
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">{t.nav.tasks}</div>
          <h1 className="font-serif text-5xl text-ag-ink mb-2">{t.tasks.title}</h1>
          <p className="text-slate-600">{t.tasks.subtitle}</p>
        </div>
        <div className="flex items-center gap-2">
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-ag-ink hover:bg-slate-800 h-11 rounded-xl" data-testid="tasks-add-btn">
              <Plus className="h-4 w-4 mr-2" />{t.tasks.addTask}
            </Button>
          </DialogTrigger>          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle className="font-serif text-2xl">{t.tasks.addTask}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4 pt-2" data-testid="tasks-form">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Titel</Label>
                <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="tasks-form-title" className="mt-1.5 h-11" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Beschreibung</Label>
                <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="tasks-form-desc" className="mt-1.5" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Kategorie</Label>
                  <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1.5 h-11" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Minuten</Label>
                  <Input type="number" min={1} value={form.estimated_minutes} onChange={(e) => setForm({ ...form, estimated_minutes: e.target.value })} className="mt-1.5 h-11" />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Abbrechen</Button>
                <Button type="submit" className="bg-ag-ink hover:bg-slate-800" data-testid="tasks-form-submit">Speichern</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </header>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="tasks-tabs" className="bg-slate-100">
          <TabsTrigger value="open" data-testid="tasks-tab-open">Offen</TabsTrigger>
          <TabsTrigger value="later" data-testid="tasks-tab-later">Später</TabsTrigger>
          <TabsTrigger value="done" data-testid="tasks-tab-done">Erledigt</TabsTrigger>
          <TabsTrigger value="all" data-testid="tasks-tab-all">Alle</TabsTrigger>
        </TabsList>
        <TabsContent value={tab} className="mt-6">
          {filtered.length === 0 ? (
            <div className="ag-card p-12 text-center" data-testid="tasks-empty">
              <div className="h-14 w-14 rounded-2xl bg-slate-100 mx-auto flex items-center justify-center mb-4">
                <ListChecks className="h-6 w-6 text-slate-500" strokeWidth={1.4} />
              </div>
              <p className="text-slate-600">{t.tasks.empty}</p>
            </div>
          ) : (
            <ul className="space-y-3">
              {filtered.map((task) => (
                <li key={task.id} data-testid={`task-${task.id}`} className="ag-card p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`ag-badge border ${PRIORITY_TONE[task.priority] || PRIORITY_TONE.normal}`}>
                        {task.priority === "high" ? t.tasks.priorityHigh : task.priority === "low" ? t.tasks.priorityLow : t.tasks.priorityNormal}
                      </span>
                      {task.auto_generated && (
                        <span className="ag-badge ag-badge-amber" data-testid={`task-${task.id}-auto`}>
                          <Sparkles className="h-3 w-3" /> Vorschlag
                        </span>
                      )}
                      <span className="text-xs text-slate-500 inline-flex items-center gap-1">
                        <Clock className="h-3 w-3" /> {t.tasks.minutes(task.estimated_minutes)}
                      </span>
                      <span className="text-xs text-slate-400">· {task.category}</span>
                    </div>
                    <div className="font-serif text-xl text-ag-ink leading-tight">{task.title}</div>
                    {task.description && <p className="text-sm text-slate-600 mt-1">{task.description}</p>}
                  </div>
                  {task.status !== "done" && (
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={() => updateStatus(task.id, "done")}
                        data-testid={`task-${task.id}-done`}
                        className="bg-emerald-600 hover:bg-emerald-700 h-10 rounded-xl"
                      >
                        <Check className="h-4 w-4 mr-1.5" /> {t.tasks.done}
                      </Button>
                      {task.status !== "later" && (
                        <Button
                          variant="outline"
                          onClick={() => updateStatus(task.id, "later")}
                          data-testid={`task-${task.id}-later`}
                          className="h-10 rounded-xl border-slate-200"
                        >
                          <Snowflake className="h-4 w-4 mr-1.5" /> {t.tasks.later}
                        </Button>
                      )}
                    </div>
                  )}
                  {task.status === "done" && (
                    task.auto_resolved ? (
                      <span className="ag-badge ag-badge-green" data-testid={`task-${task.id}-auto-resolved`}>
                        <Leaf className="h-3 w-3" /> Von LifeCore erkannt
                      </span>
                    ) : (
                      <span className="ag-badge ag-badge-green"><Check className="h-3 w-3" /> Erledigt</span>
                    )
                  )}
                </li>
              ))}
            </ul>
          )}
        </TabsContent>
      </Tabs>
    </AppLayout>
  );
}
